import { Skeleton } from 'antd';
import { Link } from 'react-router-dom';
import { useHome, type HomeAssistant, type HomeData } from '@entities/home';
import { modelsSeen } from '@entities/llm';
import { useUserStore } from '@entities/user';
import {
  IconCheck,
  IconCode,
  IconGlobe,
  IconPlane,
  IconPlus,
  LetterAvatar,
  TopBar,
  num,
  plural,
  rub,
  shortWhen,
} from '@shared/ui/v2';
import './home.css';

const STATUS: Record<HomeAssistant['status'], { label: string; color: string; text: string }> = {
  working: { label: 'Работает', color: '#16A34A', text: '#15803D' },
  stopped: { label: 'Остановлен', color: '#9CA3AF', text: '#6B7280' },
  no_balance: { label: 'Нет баланса', color: '#DC2626', text: '#B91C1C' },
};

const PLATFORM: Record<string, string> = { widget: 'Сайт', telegram: 'Telegram', max: 'MAX' };

function ChannelIcons({ ch }: { ch: HomeAssistant['channels'] }) {
  const items = [
    { on: ch.site, label: 'Сайт', icon: <IconGlobe size={15} /> },
    { on: ch.telegram, label: 'Telegram', icon: <IconPlane size={15} /> },
    { on: ch.max, label: 'MAX', icon: <span className="home-max">MAX</span> },
    { on: ch.api, label: 'API', icon: <IconCode size={15} strokeWidth={1.9} /> },
  ];
  return (
    <div role="list" aria-label="Каналы" className="home-channels">
      {items.map((i) => {
        const title = `${i.label} — ${i.on ? 'подключён' : 'не подключён'}`;
        return (
          <span
            key={i.label}
            role="listitem"
            title={title}
            className={i.on ? 'home-ch is-on' : 'home-ch'}
          >
            {i.icon}
            <span className="v2-sr">{title}</span>
          </span>
        );
      })}
    </div>
  );
}

function AssistantsTable({ data }: { data: HomeData }) {
  return (
    <section aria-labelledby="h-assist" className="home-section">
      <div className="home-section-head">
        <h2 id="h-assist" className="v2-h2">
          Ассистенты{' '}
          <span className="v2-muted" style={{ fontWeight: 500 }}>
            · {data.assistants.length}
          </span>
        </h2>
        <Link to="/assistants/new" className="v2-link">
          + Создать
        </Link>
      </div>
      <div className="v2-card" style={{ overflow: 'hidden' }}>
        {data.assistants.length === 0 ? (
          <div className="home-empty">
            <div style={{ fontWeight: 600 }}>Пока ни одного ассистента</div>
            <div className="v2-muted v2-small">Создайте первого — это займёт около двух минут.</div>
            <Link to="/assistants/new" className="v2-btn is-primary" style={{ marginTop: 8 }}>
              <IconPlus size={14} /> Новый ассистент
            </Link>
          </div>
        ) : (
          <div className="v2-table-wrap">
            <table className="v2-table" style={{ minWidth: 820 }}>
              <thead>
                <tr>
                  <th scope="col">Ассистент</th>
                  <th scope="col">Статус</th>
                  <th scope="col">Модель</th>
                  <th scope="col">База знаний</th>
                  <th scope="col">Каналы</th>
                  <th scope="col">Ответов за 7 дн.</th>
                  <th scope="col">
                    <span className="v2-sr">Действия</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.assistants.map((a) => {
                  const st = STATUS[a.status];
                  return (
                    <tr key={a.id}>
                      <td className="v2-wrap">
                        <Link to={`/assistants/${a.id}/settings`} className="home-asst">
                          <LetterAvatar name={a.name} colorKey={a.id} size={30} />
                          {a.name}
                        </Link>
                      </td>
                      <td>
                        <span className="home-status" style={{ color: st.text }}>
                          <span
                            className="v2-dot"
                            style={{ width: 7, height: 7, background: st.color }}
                          />
                          {st.label}
                        </span>
                      </td>
                      <td>{a.model?.title ?? '—'}</td>
                      <td>
                        {a.sources > 0
                          ? `${a.sources} ${plural(a.sources, 'источник', 'источника', 'источников')}`
                          : 'нет'}
                      </td>
                      <td>
                        <ChannelIcons ch={a.channels} />
                      </td>
                      <td>{num(a.answers_7d)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Link to={`/assistants/${a.id}/test-chat`} className="v2-link">
                          Тест-чат
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function NextSteps({ data }: { data: HomeData }) {
  const uid = useUserStore((st) => st.user?.id);
  const first = data.assistants[0];
  // Ведём к ассистенту, которому нужен этот шаг, иначе — к первому.
  const noKb = data.assistants.find((a) => a.sources_ready === 0) ?? first;
  const noMessenger = data.assistants.find((a) => !a.channels.telegram && !a.channels.max) ?? first;
  const steps = [
    {
      done: data.checklist.kb_tested,
      title: 'Создать ассистента, загрузить базу знаний, проверить',
      hint: 'Загрузите документы и задайте вопрос в тест-чате',
      action: noKb
        ? { to: `/assistants/${noKb.id}/knowledge`, label: 'Загрузить' }
        : { to: '/assistants/new', label: 'Создать' },
    },
    {
      done: data.checklist.messenger,
      title: 'Подключите Telegram или MAX',
      hint: 'Ответы в мессенджере и рассылки',
      action: noMessenger
        ? { to: `/assistants/${noMessenger.id}/channels`, label: 'Подключить' }
        : null,
    },
    {
      done: data.checklist.models || modelsSeen(uid),
      title: 'Сравните модели по качеству и цене',
      hint: 'Рекомендованные отобраны на реальных документах',
      action: first ? { to: `/assistants/${first.id}/settings/model`, label: 'Сравнить' } : null,
    },
  ];
  if (steps.every((s) => s.done)) return null;
  return (
    <section aria-labelledby="h-next" className="v2-card v2-card-pad home-list">
      <h2 id="h-next" className="v2-h2" style={{ marginBottom: 6 }}>
        Что сделать дальше
      </h2>
      {steps.map((s) => (
        <div key={s.title} className="home-step">
          {s.done ? (
            <span className="home-check is-done">
              <IconCheck size={12} />
            </span>
          ) : (
            <span className="home-check" />
          )}
          {s.done ? (
            <span className="home-step-done">{s.title}</span>
          ) : (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{s.title}</div>
              <div className="v2-muted v2-xs">{s.hint}</div>
            </div>
          )}
          {!s.done && s.action && (
            <Link to={s.action.to} className="v2-link">
              {s.action.label}
            </Link>
          )}
        </div>
      ))}
    </section>
  );
}

function RecentDialogs({ data }: { data: HomeData }) {
  // «Все» — диалоги ассистента из самого свежего диалога (или первого ассистента).
  const first =
    data.assistants.find((a) => a.id === data.recent_dialogs[0]?.assistant_id) ??
    data.assistants[0];
  const dialogsLink = (assistantId?: string) =>
    assistantId ? `/assistants/${assistantId}/dialogs` : '/';
  return (
    <section aria-labelledby="h-last" className="v2-card v2-card-pad home-list">
      <div className="home-section-head" style={{ marginBottom: 6 }}>
        <h2 id="h-last" className="v2-h2">
          Последние диалоги
        </h2>
        {first && (
          <Link to={dialogsLink(first.id)} className="v2-link">
            Все
          </Link>
        )}
      </div>
      {data.recent_dialogs.length === 0 && (
        <div className="v2-muted v2-small home-dialog" style={{ display: 'block' }}>
          Пока нет диалогов с клиентами. Подключите сайт, Telegram или MAX — переписка появится
          здесь.
        </div>
      )}
      {data.recent_dialogs.map((d) => (
        <Link
          key={d.id}
          to={`${dialogsLink(d.assistant_id)}?dialog=${d.id}`}
          className="home-dialog"
        >
          <span className="home-dialog-text">
            <b>{d.name || (d.platform === 'widget' ? 'Посетитель сайта' : 'Клиент')}</b> ·{' '}
            {PLATFORM[d.platform] ?? d.platform}
            {d.last_question && (
              <>
                {' · '}
                <span className="v2-muted">{d.last_question}</span>
              </>
            )}
            {d.unanswered && <span className="home-unanswered"> · без ответа</span>}
          </span>
          <span className="v2-muted" style={{ whiteSpace: 'nowrap' }}>
            {shortWhen(d.last_message_at)}
          </span>
        </Link>
      ))}
    </section>
  );
}

/** Экран 1 «Главная» (утверждён 28.10.2026). Сумм расхода нет — правило Р37. */
export function HomePage() {
  const { data, isLoading, error } = useHome();

  return (
    <>
      <TopBar title="Главная">
        <div className="v2-topbar-block">
          <div>
            <div className="v2-muted v2-xs">Баланс</div>
            <div className="home-big">{data ? rub(data.balance) : '—'}</div>
          </div>
          <Link
            to="/billing?topup=1"
            className="home-topup"
            aria-label="Пополнить баланс"
            title="Пополнить баланс"
          >
            <IconPlus size={13} />
          </Link>
        </div>
        <div className="v2-topbar-block">
          <div>
            <div className="v2-muted v2-xs">Ответов за 7 дней</div>
            <div style={{ lineHeight: 1.2 }}>
              <b style={{ fontSize: 17 }}>{data ? num(data.answers_7d) : '—'}</b>{' '}
              {data?.answers_7d_test_only && (
                <span className="v2-muted v2-xs">все в тест-чате</span>
              )}
            </div>
          </div>
        </div>
      </TopBar>
      <div className="v2-content">
        {isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
        {error && (
          <div className="v2-card v2-card-pad">Не удалось загрузить данные. Обновите страницу.</div>
        )}
        {data && (
          <>
            <AssistantsTable data={data} />
            <div className="home-grid">
              <NextSteps data={data} />
              <RecentDialogs data={data} />
            </div>
          </>
        )}
      </div>
    </>
  );
}
